import { memo } from "react";
import {
  Button,
  Caption1,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "@fluentui/react-components";
import {
  EditRegular,
  DeleteRegular,
  AlertOffRegular,
  AlertRegular,
  MoreVerticalFilled
} from "@fluentui/react-icons";

import { AlertOrderOptions, AlertWithUser, comparisonOperators } from "../../Types/alert";
import { useSignInState } from "../signInState";
import { formatDateToLocaleString } from "../../Helpers/dateUtils";
import "./AlertTable.css";
type ColumnKey =
  | "AlertTitle"
  | "MetricName"
  | "LastTriggeredAt"
  | "LastUpdatedAt"
  | "ComparisonOperator"
  | "AlertSensitivity"
  | "IsActive"
  | "CreatedBy";

const columnDefinitions: Array<{
  key: ColumnKey;
  label: string;
  sortable?: boolean;
}> = [
  { key: "AlertTitle", label: "Alert", sortable: true },
  { key: "MetricName", label: "Visualization", sortable: true },
  { key: "LastTriggeredAt", label: "Last triggered", sortable: true },
  { key: "LastUpdatedAt", label: "Last updated", sortable: true },
  { key: "ComparisonOperator", label: "Criteria", sortable: true },
  { key: "AlertSensitivity", label: "Sensitivity", sortable: true },
  { key: "IsActive", label: "Status" },
  { key: "CreatedBy", label: "Created by", sortable: true },
];

const getCriteriaValue = (alert: AlertWithUser) => {
  if(alert.ComparisonOperator && alert.ThresholdValue) {
    const operatorLabel = comparisonOperators.find(op => op.value === alert.ComparisonOperator)?.label || alert.ComparisonOperator;
    return `${operatorLabel} ${alert.ThresholdValue}`;
  }
  return "—";
};

const getColumnWidth = (columnKey: ColumnKey): string => {
  const widths: Record<ColumnKey, string> = {
    AlertTitle: "23%",
    MetricName: "15%",
    LastTriggeredAt: "12%",
    LastUpdatedAt: "12%",
    ComparisonOperator: "8%",
    AlertSensitivity: "8%",
    IsActive: "5%",
    CreatedBy: "12%",
  };
  return widths[columnKey] || "auto";
};

type AlertTableProps = {
  alerts: AlertWithUser[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  isLoading: boolean;
  sortOrder: AlertOrderOptions;
  onEdit: (alert: AlertWithUser) => void;
  onDelete: (alert: AlertWithUser) => void;
  onUpdateStatus: (alert: AlertWithUser) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (order: AlertOrderOptions) => void;
};

const AlertTable: React.FC<AlertTableProps> = ({
  alerts,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  isLoading,
  sortOrder,
  onEdit,
  onDelete,
  onUpdateStatus,
  onPageChange,
  onPageSizeChange,
  onSortChange,
}) => {
  const { signInState } = useSignInState();
  const currentUserId = signInState.userInfo[0]?.UserID;

  const handleSort = (column: ColumnKey) => {
    if (!columnDefinitions.find((col) => col.key === column)?.sortable) {
      return;
    }

    let direction: "ASC" | "DESC";
    if (sortOrder.field === column) {
      direction = sortOrder.direction === "ASC" ? "DESC" : "ASC";
    } else {
      direction = "ASC";
    }

    onSortChange({ field: column, direction });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={{ overflowX: "auto" }}>
        <Table
          aria-label="Alert management table"
          style={{ minWidth: "800px", tableLayout: "fixed" }}
        >
          <TableHeader>
            <TableRow>
              {columnDefinitions.map(({ key, label, sortable }) => {
                const isActive = sortOrder.field === key;
                
                let icon = "";
                if (isActive) {
                  icon = sortOrder.direction === "ASC" ? "▲" : "▼";
                }
                
                return (
                  <TableHeaderCell
                    key={key}
                    onClick={() => handleSort(key)}
                    style={{
                      cursor: sortable ? "pointer" : "default",
                      width: getColumnWidth(key),
                      minWidth: getColumnWidth(key),
                      textAlign: key === "IsActive" ? "center" : "left",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        justifyContent: key === "IsActive" ? "center" : "flex-start",
                        width: key === "IsActive" ? "100%" : "auto",
                      }}
                    >
                      {label}
                      {icon}
                    </span>
                  </TableHeaderCell>
                );
              })}
              <TableHeaderCell style={{ width: "80px", minWidth: "80px" }}/>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              if (isLoading) {
                return (
                  <TableRow>
                    <TableCell colSpan={columnDefinitions.length + 1}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          padding: "16px",
                        }}
                      >
                        <Spinner label="Loading alerts" />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }
              
              if (alerts.length === 0) {
                return (
                  <TableRow>
                    <TableCell colSpan={columnDefinitions.length + 1}>
                      <div style={{ padding: "16px", textAlign: "center" }}>
                        No alerts found for the selected filters.
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }
              
              return alerts.map((alert) => (
                <TableRow 
                  key={alert.AlertId}
                  className={!alert.IsActive ? "inactive-alert-row" : undefined}
                >
                  <TableCell
                    style={{
                      width: getColumnWidth("AlertTitle"),
                      minWidth: getColumnWidth("AlertTitle"),
                      overflow: "hidden",
                    }}
                  >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={alert.AlertTitle}
                        >
                          {alert.AlertTitle}
                        </span>
                        <Caption1
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={alert.AlertDescription ?? ""}
                        >
                          {alert.AlertDescription}
                        </Caption1>
                      </div>
                  </TableCell>
                  <TableCell
                    style={{
                      width: getColumnWidth("MetricName"),
                      minWidth: getColumnWidth("MetricName"),
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={alert.MetricName ?? "—"}
                  >
                    {alert.MetricName ?? "—"}
                  </TableCell>
                  <TableCell
                    style={{
                      width: getColumnWidth("LastTriggeredAt"),
                      minWidth: getColumnWidth("LastTriggeredAt"),
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={formatDateToLocaleString(alert.LastTriggeredAt)}
                  >
                    {formatDateToLocaleString(alert.LastTriggeredAt)}
                  </TableCell>
                  <TableCell
                    style={{
                      width: getColumnWidth("LastUpdatedAt"),
                      minWidth: getColumnWidth("LastUpdatedAt"),
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={formatDateToLocaleString(alert.LastUpdatedAt)}
                  >
                    {formatDateToLocaleString(alert.LastUpdatedAt)}
                  </TableCell>
                  <TableCell
                    style={{
                      width: getColumnWidth("ComparisonOperator"),
                      minWidth: getColumnWidth("ComparisonOperator"),
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={getCriteriaValue(alert)}
                  >
                    {getCriteriaValue(alert)}
                  </TableCell>
                  <TableCell
                    style={{
                      width: getColumnWidth("AlertSensitivity"),
                      minWidth: getColumnWidth("AlertSensitivity"),
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={alert.AlertSensitivity ?? "—"}
                  >
                    {alert.AlertSensitivity ?? "—"}
                  </TableCell>
                  <TableCell
                    style={{
                      width: getColumnWidth("IsActive"),
                      minWidth: getColumnWidth("IsActive"),
                      textAlign: "center",
                    }}
                  >
                    {alert.IsActive ? (
                      <AlertRegular aria-label="Active" style={{ width: "1.5rem", height: "1.5rem"}} />
                    ) : (
                      <AlertOffRegular aria-label="Inactive" style={{ width: "1.5rem", height: "1.5rem" }} />
                    )}
                  </TableCell>
                  <TableCell
                    style={{
                      width: getColumnWidth("CreatedBy"),
                      minWidth: getColumnWidth("CreatedBy"),
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={alert.CreatedByName ?? "—"}
                  >
                    {alert.CreatedByName ?? "—"}
                  </TableCell>
                  <TableCell style={{ width: "80px", minWidth: "80px" }}>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {currentUserId === alert.CreatedBy && (
                        <Menu>
                          <MenuTrigger disableButtonEnhancement>
                            <Button appearance="subtle" title="See more"><MoreVerticalFilled /></Button>
                          </MenuTrigger>
                          <MenuPopover>
                            <MenuList>
                            <MenuItem
                                icon={<EditRegular />}
                                onClick={() => onEdit(alert)}
                              >
                                Edit
                              </MenuItem>
                              <MenuItem
                                icon={alert.IsActive ? <AlertOffRegular /> : <AlertRegular />} 
                                onClick={() => onUpdateStatus(alert)}
                              >
                                {alert.IsActive ? "Deactivate" : "Activate"}
                              </MenuItem>

                              <MenuItem
                                icon={<DeleteRegular />}
                                onClick={() => onDelete(alert)}
                              >
                                Delete
                              </MenuItem>
                            </MenuList>
                          </MenuPopover>
                        </Menu>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ));
            })()}
          </TableBody>
        </Table>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Caption1>Rows per page</Caption1>
          <select
            aria-label="Rows per page"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            style={{ padding: "4px 8px" }}
          >
            {[10, 25, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Caption1>
            Page {currentPage} of {totalPages}
          </Caption1>
          <Button
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default memo(AlertTable);
